import type {
  ConnectingDelayJudgementMode,
  ConnectingDirectionEntry,
  ConnectingLineEntry,
  DelayBroadcastPattern,
} from '../../../api/types';
import type { IStationsLine } from '../../../data/istationsLines';
import type { RouteMaster } from '../../../data/routes';
import type { RouteStop } from '../../../data/routeStops';

import {
  CONNECTING_STATION_LINE_DEFAULT_SSML,
  DEFAULT_CONNECTING_STATION_DELAY_PATTERNS,
} from '../../../utils/broadcastContents';
// 数値入力は共通の NumericInput を使う。
// 素の <input type="number" value={number}> だと Number('') === 0 のため
// 既定値 0 を消せない不具合が起きる (NumericInput 側のコメント参照)。
import { clampNonNegativeInt, NumericInput } from '../../NumericInput';
import { CONNECTING_DELAY_MODE_META } from '../constants';
import { IStationsLinesSelect } from '../parts/IStationsLinesSelect';
import { PlaceholderLegend } from '../parts/PlaceholderLegend';

import { CONNECTING_STATION_LINE_PLACEHOLDERS } from '../placeholders';
import { ConnectingDirectionsSection } from './ConnectingDirectionsSection';
import { ConnectingStationLineDelayPatternsTable } from './ConnectingStationLineDelayPatternsTable';

/**
 * 接続線遅延 (乗換駅ベース / part2) の 1 接続線ぶんのブロック。
 * ConnectingLineDelayByStationFields.lines[i] を編集する。
 *
 * 内部で「route_id 選択 → 判定モード → 乗換時間 (transfer_aware) → 方面 (1〜2件) →
 * 接続線ブロック SSML → 遅延放送パターン (方面部分文SSML)」を並べる。
 * 判定モードによる項目表示切替のルールは part1 (`ConnectingLineDelayFields`) と同じ。
 */
export function ConnectingLineBlock({
  index,
  line,
  routes,
  routeStops,
  istationsLines = [],
  isOtherMode = false,
  canRemove,
  isFirst,
  isLast,
  onChange,
  onAdd,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  index: number;
  line: ConnectingLineEntry;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  /**
   * 「運行情報の参照元（アイステイションズ路線）」の選択肢マスタ。
   * ATOS 線区マスタ (`routes`) とは独立した別系統で、連動させない。
   */
  istationsLines?: IStationsLine[];
  /**
   * 親コンテンツが「その他」モード (`transfer_stop_mode === 'other'`) かどうか。
   *
   * `true` のときは遅延判定を一切行わず運行情報のみを放送するため、
   * 接続線 route_id / 線区愛称 / 判定モード / 乗換時間 / 方面 / 遅延放送パターン /
   * 接続線ブロック SSML をすべて非表示にし、
   * **アイステイションズ路線の選択のみ** を表示する。
   */
  isOtherMode?: boolean;
  canRemove: boolean;

  isFirst: boolean;
  isLast: boolean;
  onChange: (next: ConnectingLineEntry) => void;
  onAdd: () => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}): React.JSX.Element {
  const routeId = line.route_id ?? '';
  const judgementMode: ConnectingDelayJudgementMode = line.delay_judgement_mode ?? 'whole_line';
  const transferTimeSeconds = line.transfer_time_seconds ?? 180;
  const lineTemplate = line.ssml_line_template ?? CONNECTING_STATION_LINE_DEFAULT_SSML;
  const directions: ConnectingDirectionEntry[] = Array.isArray(line.directions)
    ? line.directions
    : [];
  const patterns: DelayBroadcastPattern[] = Array.isArray(line.delay_patterns)
    ? line.delay_patterns
    : DEFAULT_CONNECTING_STATION_DELAY_PATTERNS.map((p) => ({ ...p }));

  // 判定モードによる項目表示切替 (part1 と同じ考え方)。
  //   whole_line       : 方面ドロップダウン (direction_id) / 区間 (start/end_stop_id) は非表示。
  //                      direction_name (放送文用) だけは表示 (方面別に案内する用途は残るため)。
  //   section_scheduled: 方面ごとに区間 (start/end_stop_id) 必須。
  //   transfer_aware   : 乗換時間 (秒) 必須。区間は不要。
  const isTransferAwareMode = judgementMode === 'transfer_aware';

  // route_id マスタに未登録の値も残す (part1 と同じ)。
  const isUnknownRoute = routeId !== '' && !routes.some((r) => r.route_id === routeId);

  // 表示ラベル用: 接続線の一般的な線名 (routes-master.json の route_name)。
  const currentRoute = routes.find((r) => r.route_id === routeId);
  const routeDisplayName = currentRoute?.route_name || routeId;

  /** ブロック全体を patch で更新 */
  const emit = (patch: Partial<ConnectingLineEntry>): void => {
    onChange({ ...line, ...patch });
  };

  return (
    <div className="connecting-line-block" data-testid={`connecting-line-block-${index}`}>
      {/* ブロックヘッダ: ラベル + 操作ボタン (上/下/削除/追加) */}
      <div
        className="connecting-line-block-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 6,
          flexWrap: 'wrap',
        }}
      >
        <strong>
          接続線 #{index + 1}
          {routeId ? `（${routeDisplayName}）` : ''}
        </strong>
        <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
          <button
            type="button"
            aria-label={`接続線#${index + 1} を上へ`}
            data-testid={`connecting-line-move-up-${index}`}
            disabled={isFirst}
            onClick={onMoveUp}
          >
            ↑
          </button>
          <button
            type="button"
            aria-label={`接続線#${index + 1} を下へ`}
            data-testid={`connecting-line-move-down-${index}`}
            disabled={isLast}
            onClick={onMoveDown}
          >
            ↓
          </button>
          <button
            type="button"
            aria-label={`接続線#${index + 1} の後に追加`}
            data-testid={`connecting-line-add-${index}`}
            onClick={onAdd}
          >
            ＋ 追加
          </button>
          {canRemove ? (
            <button
              type="button"
              className="danger"
              aria-label={`接続線#${index + 1} を削除`}
              data-testid={`connecting-line-remove-${index}`}
              onClick={onRemove}
            >
              × 削除
            </button>
          ) : null}
        </div>
      </div>

      {/*
        「接続線 route_id」と「接続線 線区愛称（任意）」は二段組 (横並び) にする。
        どちらも 1 行で収まる短い項目であり、縦積みにすると接続線ブロックが
        不要に縦長になるため `.field-pair-2col` で囲む
        (狭幅では CSS side で 1 列へフォールバックする)。

        「その他」モードでは遅延判定を行わないため route_id / 線区愛称は使わない。
        (放送内容はアイステイションズ路線の運行情報の原文のみ)
      */}
      {isOtherMode ? null : (
        <div className="field-pair-2col" data-testid={`connecting-line-route-pair-${index}`}>
          {/* 左セル: 接続線 route_id */}
          <label>
            <span className="required">接続線 route_id</span>
            <select
              aria-label={`接続線#${index + 1} route_id`}
              data-testid={`connecting-line-route-id-${index}`}
              value={routeId}
              onChange={(e) =>
                emit({
                  route_id: e.target.value,
                  // 路線を変えたら方面の区間駅はリセット (別路線の stop_id が残らないように)
                  directions: directions.map((d) => ({
                    ...d,
                    start_stop_id: undefined,
                    end_stop_id: undefined,
                  })),
                })
              }
            >
              <option value="">(選択してください)</option>
              {routes.map((r) => (
                <option key={r.route_id} value={r.route_id}>
                  {r.route_id}
                </option>
              ))}
              {isUnknownRoute ? <option value={routeId}>{routeId} (マスタ未登録)</option> : null}
            </select>
          </label>

          {/*
          右セル: 接続線ブロック単位の線区愛称 (任意)。設定時は放送 SSML の
          {route_name} / {route} プレースホルダに最優先で使用される
          (route_name → f"{route_id}線" にフォールバック)。
          空文字は「未設定扱い」で、正規化は Lambda 側 (_normalize_contents) で行う。
        */}
          <label>
            <span>接続線 線区愛称（任意）</span>
            <input
              type="text"
              aria-label={`接続線#${index + 1} 線区愛称`}
              data-testid={`connecting-line-route-nickname-${index}`}
              value={typeof line.route_nickname === 'string' ? line.route_nickname : ''}
              onChange={(e) => emit({ route_nickname: e.target.value })}
              placeholder="例: 湘南新宿ライン"
            />
            <span className="hint" style={{ fontSize: 12, color: '#555' }}>
              設定すると <code>{'{route_name}'}</code> / <code>{'{route}'}</code>{' '}
              プレースホルダに最優先で使用されます。
              未入力の場合は従来通り一般的な線名で案内します。
            </span>
          </label>
        </div>
      )}

      {/*
        運行情報の参照元 (アイステイションズ路線) の複数選択。
        「その他」モードではこの項目だけが唯一の設定項目となる。

        part2 は接続線ごと (lines[].istations_lines) に保持する。
        上の「接続線 route_id」(ATOS) とは連動させず、一致しない路線も自由に選べる。
      */}
      <IStationsLinesSelect
        testIdPrefix={`connecting-line-istations-lines-${index}`}
        ariaLabelPrefix={`接続線#${index + 1} `}
        options={istationsLines}
        value={Array.isArray(line.istations_lines) ? line.istations_lines : []}
        onChange={(nextLines) =>
          // 空配列は「未設定」として落とし、DB / JSON にゴミを残さない
          // (サーバ側も空配列はキーごと保存しない)。
          emit({ istations_lines: nextLines.length > 0 ? nextLines : undefined })
        }
      />

      {/*
        ここから下 (判定モード / 乗換時間 / 方面 / ブロック SSML / 遅延放送パターン) は
        すべて **遅延判定と遅延放送文の組み立て** に関する設定なので、
        「その他」モード (運行情報の原文のみを放送) では丸ごと非表示にする。
      */}
      {isOtherMode ? null : (
        <>
          {/* 判定モード */}

          <label>
            <span className="required">判定モード</span>

            <select
              aria-label={`接続線#${index + 1} 判定モード`}
              data-testid={`connecting-line-judgement-mode-${index}`}
              value={judgementMode}
              onChange={(e) => {
                const nextMode = e.target.value as ConnectingDelayJudgementMode;
                // モード切替時にモード固有項目をリセットする。
                // section_scheduled 以外なら方面ごとの区間駅は不要 (undefined に落とす)。
                const nextDirections =
                  nextMode === 'section_scheduled'
                    ? directions
                    : directions.map((d) => ({
                        ...d,
                        start_stop_id: undefined,
                        end_stop_id: undefined,
                      }));
                emit({
                  delay_judgement_mode: nextMode,
                  directions: nextDirections,
                });
              }}
            >
              {CONNECTING_DELAY_MODE_META.map((m) => (
                <option key={m.mode} value={m.mode}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <p className="hint" style={{ margin: '4px 0' }}>
            {judgementMode === 'whole_line'
              ? '接続線全体の最大遅延で判定します。方面ごとの区間指定は不要ですが、案内文面用の方面名 (下で入力) は必要です。'
              : judgementMode === 'section_scheduled'
                ? '指定区間に定刻在線している便の遅延で判定します。方面ごとに区間 (開始駅・終了駅) を指定してください。'
                : 'お客さまがまさに乗り換えようとしている接続列車の遅延で判定します。乗換時間 (秒) が必須です。'}
          </p>

          {/* 乗換時間 (transfer_aware のみ) */}
          {isTransferAwareMode ? (
            <label>
              <span className="required">乗換時間 (秒)</span>
              <NumericInput
                min={0}
                step={1}
                aria-label={`接続線#${index + 1} 乗換時間 (秒)`}
                data-testid={`connecting-line-transfer-time-${index}`}
                value={transferTimeSeconds}
                normalizeValue={clampNonNegativeInt}
                onChange={(nextTransferTime) => emit({ transfer_time_seconds: nextTransferTime })}
              />
            </label>
          ) : null}

          {/* 方面ブロック (1〜2件) */}
          <ConnectingDirectionsSection
            lineIndex={index}
            routeId={routeId}
            judgementMode={judgementMode}
            routes={routes}
            routeStops={routeStops}
            directions={directions}
            onChangeDirections={(nextDirections) => emit({ directions: nextDirections })}
          />

          {/* 接続線ブロック SSML テンプレート */}
          <label>
            <span className="required">接続線ブロック SSML テンプレート</span>
            <input
              type="text"
              aria-label={`接続線#${index + 1} ブロック SSML テンプレート`}
              data-testid={`connecting-line-block-template-${index}`}
              required
              value={lineTemplate}
              placeholder={CONNECTING_STATION_LINE_DEFAULT_SSML}
              onChange={(e) => emit({ ssml_line_template: e.target.value })}
            />
          </label>
          <PlaceholderLegend
            testId={`connecting-line-block-template-hint-${index}`}
            items={CONNECTING_STATION_LINE_PLACEHOLDERS}
          />

          {/* 遅延放送パターン (方面部分文の SSML) */}
          <ConnectingStationLineDelayPatternsTable
            lineIndex={index}
            patterns={patterns}
            onChange={(nextPatterns) => emit({ delay_patterns: nextPatterns })}
          />
        </>
      )}
    </div>
  );
}
